/*
 * Purpose: Dynamic SEO head — title, meta, canonical, Open Graph, Twitter per route
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoForPath, SEO_SITE } from '@/lib/seoMeta';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    document.title = seo.title;

    upsertMeta('name', 'description', seo.description);
    if (seo.keywords) upsertMeta('name', 'keywords', seo.keywords);
    upsertMeta('name', 'robots', seo.robots);
    upsertMeta('name', 'author', 'Teknoled-G Limited — Autoblocks.run');
    upsertMeta('name', 'application-name', SEO_SITE.name);

    upsertLink('canonical', seo.canonical);

    let alternate = document.head.querySelector('link[rel="alternate"][hreflang="x-default"]') as HTMLLinkElement | null;
    if (!alternate) {
      alternate = document.createElement('link');
      alternate.rel = 'alternate';
      alternate.hreflang = 'x-default';
      document.head.appendChild(alternate);
    }
    alternate.href = SEO_SITE.marketingUrl;
    alternate.title = `${SEO_SITE.brand} marketing`;

    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:type', seo.ogType);
    upsertMeta('property', 'og:url', seo.canonical);
    upsertMeta('property', 'og:site_name', SEO_SITE.name);
    upsertMeta('property', 'og:locale', SEO_SITE.locale);
    upsertMeta('property', 'og:image', SEO_SITE.ogImage);

    upsertMeta('name', 'twitter:card', SEO_SITE.twitterCard);
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', SEO_SITE.ogImage);

    upsertMeta('name', 'theme-color', '#0f172a');
  }, [pathname]);

  return null;
}

export default SeoHead;
